import React from 'react';
import { Newspaper, Megaphone, Cog, Bell, X, BarChart3, ShieldAlert, Zap } from 'lucide-react';
import { NotificationItem, NotificationCategory, resolvePresentation, isBreakingNews, BREAKING_NEWS_GRADIENT, BREAKING_NEWS_INK } from '../../composables/notificationAPI';

const CATEGORY_ICONS: Partial<Record<NotificationCategory, React.ComponentType<{ size?: number }>>> = {
  ARTICLE: Newspaper,
  ANNOUNCEMENT: Megaphone,
  PORTFOLIO: BarChart3,
  SYSTEM: Cog,
  SECURITY: ShieldAlert,
};

// title มาจาก backend เป็น string ล้วน (เช่น "RKLB แตะ $74.25 แล้ว!") — แยกส่วนที่เป็นราคา $xx.xx ออกมาทาสีเหลือง
const PRICE_PATTERN = /(\$[\d,]+(?:\.\d+)?)/g;

function highlightPrices(text: string): React.ReactNode {
  return text.split(PRICE_PATTERN).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} style={{ color: 'var(--yellow-text-color)' }}>{part}</span>
    ) : (
      part
    )
  );
}

const NewNotificationToast: React.FC<{
  item: NotificationItem;
  visible: boolean;
  onClick: () => void;
  onDismiss: () => void;
}> = ({ item, visible, onClick, onDismiss }) => {
  const breaking = isBreakingNews(item.type);
  const CategoryIcon = breaking ? Zap : CATEGORY_ICONS[item.category] ?? Bell;
  const presentation = resolvePresentation(item);

  return (
    <div
      onClick={onClick}
      role="button"
      className={`relative flex items-start gap-4 w-[26rem] max-w-[92vw] p-4 rounded-2xl cursor-pointer border transition-all duration-300 ${visible ? 'opacity-100 translate-y-0 ' : 'opacity-0 -translate-y-2'}`}
      style={breaking ? {
        // กรอบไล่สีด้วย background 2 ชั้น (padding-box ทึบ + border-box ไล่สี) + แสงเขียวจาง ๆ มุมซ้ายบน
        border: '1.5px solid transparent',
        background: `radial-gradient(120% 90% at 0% 0%, rgba(34,197,94,0.16), transparent 55%) padding-box,
          linear-gradient(var(--component-bg-color), var(--component-bg-color)) padding-box,
          ${BREAKING_NEWS_GRADIENT} border-box`,
        color: 'var(--text-color)',
        marginTop: '60px',
        boxShadow: '0 20px 45px rgba(0,0,0,0.55), 0 0 36px 4px rgba(34,197,94,0.35)',
      } : {
        backgroundColor: 'var(--component-bg-color)',
        color: 'var(--text-color)',
        borderColor: 'rgba(255,255,255,0.12)',
        marginTop: '60px',
        boxShadow: '0 20px 45px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05), 0 0 28px 4px rgba(34,197,94,0.25)',
      }}
    >
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismiss(); }}
        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full opacity-60 hover:opacity-100 transition-opacity"
        style={{ backgroundColor: 'var(--bg-item)' }}
        title="ปิด"
      >
        <X size={13} />
      </button>

      <div
        className="w-14 h-14 rounded-lg overflow-hidden shrink-0"
        style={{ background: breaking ? BREAKING_NEWS_GRADIENT : 'var(--bg-navbar, #e5e7eb)' }}
      >
        {item.image ? (
          <img src={item.image} alt="" className="w-full h-full object-cover" />
        ) : presentation.icon ? (
          // icon จาก backend เป็น emoji เดี่ยว ๆ (เช่น "📊") — render เป็น text ตรง ๆ
          <div className="w-full h-full flex items-center justify-center text-2xl">{presentation.icon}</div>
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ color: breaking ? BREAKING_NEWS_INK : 'var(--secondary-text-color)' }}>
            <CategoryIcon size={22} {...(breaking ? { strokeWidth: 2.5 } : {})} />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1 pr-5">
        {breaking ? (
          <div className="flex items-center gap-1.5 mb-1">
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-extrabold px-2 py-0.5 rounded-full"
              style={{ background: BREAKING_NEWS_GRADIENT, color: BREAKING_NEWS_INK }}
            >
              <span className="relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full opacity-60 motion-safe:animate-ping" style={{ backgroundColor: BREAKING_NEWS_INK }} />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ backgroundColor: BREAKING_NEWS_INK }} />
              </span>
              ข่าวด่วน
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mb-0.5">
            <Bell size={13} style={{ color: 'var(--green-text-color)' }} />
            <span className="text-xs font-bold opacity-60">{presentation.label}</span>
          </div>
        )}
        <div className="text-base font-bold truncate">{highlightPrices(item.title)}</div>
        <p className="text-sm opacity-75 mt-0.5 line-clamp-2 whitespace-pre-wrap">{item.body}</p>
      </div>
    </div>
  );
};

export default NewNotificationToast;
